import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUsers, updateUserStatus } from '../services/userService';
import { GENDER } from '../constants/enums';
import InstagramIcon from '../components/InstagramIcon';

const PAGE_SIZE = 10;

const routeStatusMap = {
  '/users': '',
  '/users/active': 'active',
  '/users/inactive': 'inactive',
  '/users/blocked': 'blocked',
  '/users/pending': 'pending_approval',
};

const routeTitleMap = {
  '/users': 'All Users',
  '/users/active': 'Active Users',
  '/users/inactive': 'Inactive Users',
  '/users/blocked': 'Blocked Users',
  '/users/pending': 'Pending Approval',
};

const genderMap = GENDER;

function getStatus(u) {
  if (u.isBlocked === 1) return 'Blocked';
  if (u.accountStatus === 1) return 'Pending';
  if (u.isActive === 0) return 'Inactive';
  return 'Active';
}

function calcAge(dateOfBirth) {
  if (!dateOfBirth) return '-';
  const diff = Date.now() - new Date(dateOfBirth).getTime();
  const age = Math.floor(diff / 31557600000);
  return age > 0 && age < 150 ? age : '-';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getReferredBy(user) {
  const referrer =
    user?.referredBy ||
    user?.referredByUser ||
    user?.referrer ||
    user?.referredUser ||
    null;

  if (!referrer || typeof referrer !== 'object') return null;

  const name = referrer.name || referrer.fullName || '-';
  const email = referrer.email || referrer.emailAddress || '-';

  if (name === '-' && email === '-') return null;

  return { name, email };
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

function resolveTotalPages(pagination) {
  const pag = pagination || {};
  if (pag.totalPages != null && Number(pag.totalPages) > 0) {
    return Number(pag.totalPages);
  }
  const total = Number(pag.total ?? pag.totalCount ?? pag.totalItems);
  if (total > 0) {
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }
  return 1;
}

export default function Users() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeStatus = routeStatusMap[location.pathname] || '';
  const pageTitle = routeTitleMap[location.pathname] || 'All Users';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(routeStatus);
  const [genderFilter, setGenderFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [blockTarget, setBlockTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);

  const activeStatus = routeStatus || statusFilter;

  // Debounce search input so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getUsers(page, PAGE_SIZE, activeStatus || undefined, genderFilter || undefined, debouncedSearch || undefined);
      const list = Array.isArray(response.data) ? response.data : [];
      setUsers(list);
      setTotalPages(resolveTotalPages(response.pagination));
    } catch (err) {
      setError(err.message || 'Failed to fetch users.');
      setUsers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, activeStatus, genderFilter, debouncedSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setStatusFilter('');
    setGenderFilter('');
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }, [routeStatus]);

  const pageItems = getPageItems(page, totalPages);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    setPage(nextPage);
  }

  async function confirmToggleBlock() {
    const user = blockTarget;
    if (!user) return;
    const newStatus = user.isBlocked === 1 ? 'unblocked' : 'blocked';
    setBlockTarget(null);
    try {
      await updateUserStatus(user.id, newStatus);
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to update user status.');
    }
  }

  async function confirmApprove() {
    const user = approveTarget;
    if (!user) return;
    setApproveTarget(null);
    try {
      await updateUserStatus(user.id, 'approved');
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to approve user.');
    }
  }

  return (
    <div className="users-page main-bg-top">
      <div className="users-header">
        <h2>{pageTitle}</h2>
        <div className="users-filters">
          <div className="users-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={genderFilter}
              onChange={(e) => {
                setGenderFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Genders</option>
              <option value="1">Female</option>
              <option value="2">Male</option>
              <option value="3">Other</option>
            </select>
          </div>
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              disabled={!!routeStatus}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
              <option value="pending_approval">Pending Approval</option>
            </select>
          </div>
        </div>
      </div>

      <div className="users-table-scroll">
        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Referred By</th>
                <th>Gender</th>
                <th>Age</th>
                <th>Location</th>
                <th>Petals</th>
                <th>Joined Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="users-empty">Loading users...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="9" className="users-empty">{error}</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="9" className="users-empty">No users found.</td>
                </tr>
              ) : (
                users.map((u) => {
                  const status = getStatus(u);
                  const gender = genderMap[u.gender] || '-';
                  const age = calcAge(u.dateOfBirth);
                  const location = u.locationCity || '-';
                  const referredBy = getReferredBy(u);

                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {u.image?.url ? (
                              <img
                                src={u.image.url}
                                alt={u.name || 'User'}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.textContent = u.name ? u.name.charAt(0).toUpperCase() : '?';
                                }}
                              />
                            ) : (
                              <span className="avatar-placeholder">
                                {u.name ? u.name.charAt(0).toUpperCase() : '?'}
                              </span>
                            )}
                          </div>
                          <div className="user-cell-info">
                            <span className="user-cell-name">{u.name}</span>
                            <span className="user-cell-email">{u.email}</span>
                            {u.instagramUrl && (
                              <a
                                href={u.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="user-instagram-link user-instagram-link-sm"
                                title="Open Instagram profile"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <InstagramIcon size={13} />
                                <span className="user-instagram-username">
                                  @{u.instagramUsername || u.profileInsta}
                                </span>
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        {referredBy ? (
                          <div className="user-cell-info">
                            <span className="user-cell-name">{referredBy.name}</span>
                            <span className="user-cell-email">{referredBy.email}</span>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{gender}</td>
                      <td>{age}</td>
                      <td>
                        <div className="location-cell">
                          <img alt="Location" className="menu-icon-img" src={`${process.env.PUBLIC_URL}/images/location-icon.svg`} />
                          {location}
                        </div>
                      </td>
                      <td>
                        <div className="petals-cell">
                          <img alt="Petals" className="menu-icon-img" src={`${process.env.PUBLIC_URL}/images/petals.svg`} /> {u.petals || 0}
                        </div>
                      </td>
                      <td>{formatDate(u.createdAt)}</td>
                      <td>
                        <span className={`status-badge status-${status.toLowerCase()}`}>{status}</span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button className="action-btn" title="View" onClick={() => navigate(`/users/${u.id}`)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          {status === 'Pending' && (
                            <button
                              className="approve-text-btn"
                              onClick={() => setApproveTarget(u)}
                            >
                              Approve
                            </button>
                          )}
                          {status === 'Active' && (
                            <button
                              className="action-btn"
                              title="Block"
                              onClick={() => setBlockTarget(u)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                              </svg>
                            </button>
                          )}
                          {status === 'Blocked' && (
                            <button
                              className="action-btn"
                              title="Unblock"
                              onClick={() => setBlockTarget(u)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                                <path d="M9 12l2 2 4-4"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {blockTarget && (
        <div className="modal-overlay" onClick={() => setBlockTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{blockTarget.isBlocked === 1 ? 'Unblock User?' : 'Block User?'}</h3>
            <p className="modal-message">
              Are you sure you want to {blockTarget.isBlocked === 1 ? 'unblock' : 'block'} <br />
              {blockTarget.name}?
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={() => setBlockTarget(null)}>
                Cancel
              </button>
              <button className="modal-btn modal-btn-confirm" onClick={confirmToggleBlock}>
                {blockTarget.isBlocked === 1 ? 'Unblock' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}

      {approveTarget && (
        <div className="modal-overlay" onClick={() => setApproveTarget(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Approve User?</h3>
            <p className="modal-message">
              Are you sure you want to approve <br />
              {approveTarget.name}?
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={() => setApproveTarget(null)}>
                Cancel
              </button>
              <button className="modal-btn modal-btn-confirm" onClick={confirmApprove}>
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="users-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
